import * as React from 'react';
import {
  getGetAccessContextLogoQueryKey,
  useGetAccessContextLogo
} from '@cdf/api-client-react';
import { LoaderCircleIcon, ChevronDownIcon } from 'lucide-react';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { ScopeChoices } from './scope-picker';

import { useTenantEntity } from '@/components/access/tenant-entity-provider';
import { cn } from '@/lib/utils';

export function allowTenantEntityScopeChange() {
  return typeof window === 'undefined' || window.dispatchEvent(new Event('cdf:before-scope-change', { cancelable: true }));
}

export function TenantEntityIndicator() {
  const { selected, snapshot, status, contextsLoading } = useTenantEntity();
  const [picker, setPicker] = React.useState<'tenant' | 'entity' | null>(null);
  const logoQuery = useGetAccessContextLogo({
    query: {
      queryKey: [
        ...getGetAccessContextLogoQueryKey(),
        selected?.tenantId ?? 'unselected',
        selected?.entityId ?? 'unselected',
        snapshot?.logoAssetId ?? 'no-logo'
      ],
      enabled: Boolean(selected && snapshot?.logoAssetId),
      staleTime: Infinity
    }
  });
  const logoUrl = React.useMemo(
    () => logoQuery.data ? URL.createObjectURL(logoQuery.data) : null,
    [logoQuery.data, snapshot?.logoAssetId]
  );
  const [loadedLogoUrl, setLoadedLogoUrl] = React.useState<string | null>(null);
  const [failedLogoUrl, setFailedLogoUrl] = React.useState<string | null>(null);

  React.useEffect(() => () => {
    if (logoUrl) URL.revokeObjectURL(logoUrl);
  }, [logoUrl]);

  if (!selected) return null;

  const loading = contextsLoading || status === 'loading' || status === 'switching';
  const label = `${selected.tenantName} · ${selected.entityName}`;
  const initials = selected.entityName
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0]?.toUpperCase())
    .join('') || '?';
  const canRenderLogo = Boolean(logoUrl && failedLogoUrl !== logoUrl);
  const logoLoaded = Boolean(logoUrl && loadedLogoUrl === logoUrl);

  return (
    <div
      aria-label="Active tenant and entity"
      data-testid="tenant-entity-indicator"
      data-tenant-name={selected.tenantName}
      data-entity-name={selected.entityName}
      title={label}
      className="flex min-w-0 flex-1 items-center gap-1 sm:gap-2"
    >
      <div className="relative size-9 shrink-0">
        {!logoLoaded && (
          <span
            className="bg-muted text-muted-foreground flex size-9 items-center justify-center rounded-md text-xs font-semibold"
            aria-hidden
          >
            {initials}
          </span>
        )}
        {canRenderLogo && (
          <img
            src={logoUrl!}
            alt={`${selected.entityName} logo`}
            className={cn(
              'bg-background absolute inset-0 size-9 rounded-md border object-contain p-0.5',
              !logoLoaded && 'opacity-0'
            )}
            onLoad={() => setLoadedLogoUrl(logoUrl)}
            onError={() => setFailedLogoUrl(logoUrl)}
          />
        )}
      </div>
      <div className="min-w-0">
        <span className="block text-xs text-muted-foreground">Tenant / Entity</span>
        <div className="flex min-w-0 items-center">
          {(['tenant', 'entity'] as const).map(kind => <React.Fragment key={kind}>
            {kind === 'entity' && <span className="text-muted-foreground" aria-hidden>/</span>}
            <DropdownMenu open={picker === kind} onOpenChange={open => setPicker(open ? kind : null)}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" disabled={loading} className="h-8 min-w-0 max-w-16 shrink gap-1 px-1 sm:max-w-40 lg:max-w-56" aria-label={`Choose ${kind}`}>
                  <span className="truncate">{kind === 'tenant' ? selected.tenantName : selected.entityName}</span><ChevronDownIcon className="size-3 shrink-0" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-[min(18rem,calc(100vw-1rem))]"><ScopeChoices kind={kind} close={() => setPicker(null)} /></DropdownMenuContent>
            </DropdownMenu>
          </React.Fragment>)}
        </div>
      </div>
      {loading && (
        <span role="status" aria-label="Updating active tenant and entity">
          <LoaderCircleIcon className="size-3.5 animate-spin" aria-hidden />
        </span>
      )}
    </div>
  );
}
